import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { IoChevronDown } from 'react-icons/io5';
import LanguageContext from '../../context/LanguageContext.js';
import { LANGUAGE_FLAGS, LANGUAGE_NAMES } from '../../config/languages.js';
import './LanguageDropdown.css';

function LanguageDropdown({ className = '' }) {
    const { currentLanguage, changeLanguage, supportedLanguages } = useContext(LanguageContext);

    const languages = useMemo(
        () =>
            supportedLanguages.map((code) => ({
                code,
                name: LANGUAGE_NAMES[code],
                flag: LANGUAGE_FLAGS[code],
            })),
        [supportedLanguages]
    );

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // A single-language store has nothing to switch between.
    if (languages.length < 2 || !currentLang) return null;

    const handleLanguageSelect = (langCode) => {
        changeLanguage(langCode);
        setDropdownOpen(false);
    };

    return (
        <div className={`custom-language-selector ${className}`.trim()} ref={dropdownRef}>
            <div
                className="language-selector-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                role="button"
                tabIndex={0}
                aria-expanded={dropdownOpen}
                aria-haspopup="listbox"
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setDropdownOpen(!dropdownOpen);
                    }
                }}
            >
                <img
                    src={currentLang.flag}
                    className="flag-img"
                    alt={`${currentLang.name} flag`}
                />
                <span className="language-name">{currentLang.name}</span>
                <IoChevronDown className={`dropdown-icon ${dropdownOpen ? 'open' : ''}`} />
            </div>

            {dropdownOpen && (
                <div className="language-dropdown" role="listbox">
                    {languages.map((lang) => (
                        <div
                            key={lang.code}
                            className={`language-option ${currentLanguage === lang.code ? 'active' : ''}`}
                            onClick={() => handleLanguageSelect(lang.code)}
                            role="option"
                            aria-selected={currentLanguage === lang.code}
                        >
                            <img
                                src={lang.flag}
                                className="flag-img"
                                alt={`${lang.name} flag`}
                            />
                            <span>{lang.name}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default LanguageDropdown;
